import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { ApiError } from "../../../../services/apiClient";
import {
  createUser,
  getUser,
  listUserOAuthConnections,
  unlinkUserOAuthProvider,
  updateUser,
  updateUserStatus,
  type AdminCreateUserPayload,
  type AdminUpdateUserPayload,
} from "../../api/users";
import type {
  AddressPayload,
  OAuthConnection,
  User,
  UserStatus,
} from "../../../../types/auth";

import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";

import {
  ArrowLeft,
  FloppyDisk,
  LinkSimpleBreak,
  SquaresFourIcon,
  XCircle,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../../shared/ui/icons";
import { FormFieldLabel } from "../../../../components/FormFieldLabel";
import { ConnectedAccountProvider } from "../../../../components/ConnectedAccountProvider";
import {
  GeoLocationSelect,
  type GeoSelection,
} from "../../../../components/geo/GeoLocationSelect";
import { ConfirmDialog } from "../../../../components/ConfirmDialog";
import { useFormFeedback } from "../../../../hooks/useFormFeedback";
import { storage } from "../../../shared/api/storage";
import { ADMIN_USERS_NOTICE_KEY } from "./constants";
import { useFormState } from "../../../../hooks/useFormState";
import { createLogger } from "../../../../utils/logger";

const logger = createLogger("AdminUserFormPage");

const ROLE_OPTIONS: User["system_role"][] = ["admin", "user"];
type ManagedUserStatus = Extract<UserStatus, "active" | "suspended" | "disabled">;
const MANAGED_USER_STATUSES: ManagedUserStatus[] = ["active", "suspended", "disabled"];

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google",
  facebook: "Facebook",
  github: "GitHub",
  microsoft: "Microsoft",
};

const getProviderLabel = (provider: string) => {
  const normalized = provider.toLowerCase();
  if (PROVIDER_LABELS[normalized]) {
    return PROVIDER_LABELS[normalized];
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

type RouteState = {
  user?: User;
};

type FormValues = {
  email: string;
  name: string;
  password: string;
  system_role: User["system_role"];
};

const createEmptyForm = (): FormValues => ({
  email: "",
  name: "",
  password: "",
  system_role: "user"
});

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const formStackClasses = "flex flex-col gap-8";
const gridClasses = "grid grid-cols-1 gap-6 sm:grid-cols-2";
const fieldClasses = "flex flex-col gap-2";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const ghostButtonClasses = "pm-btn pm-btn-ghost";

export function AdminUserFormPage() {
  const { t, i18n } = useTranslation();
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId?: string }>();

  const isEditing = Boolean(userId);

  const initialFormState = useMemo(() => createEmptyForm(), []);
  const [form, formActions] = useFormState<FormValues>(initialFormState);
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(isEditing);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { error, success, setError, setSuccess, resetFeedback } = useFormFeedback();
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] =
    useState<boolean>(isEditing);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<
    string | null
  >(null);
  const [confirmAction, setConfirmAction] = useState<
    | { type: "disconnect-provider"; provider: string }
    | { type: "cancel" }
    | null
  >(null);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressPostalCode, setAddressPostalCode] = useState("");
  const [addressSiteDescription, setAddressSiteDescription] = useState("");
  const [addressGeo, setAddressGeo] = useState<GeoSelection>({});
  const [addressCoordinates, setAddressCoordinates] = useState<{
    latitude?: number;
    longitude?: number;
  }>({});
  const [statusForm, setStatusForm] = useState<{ status: ManagedUserStatus; reason: string }>({
    status: "active",
    reason: "",
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const syncAddressFromUser = useCallback((source?: User) => {
    setAddressLine1(source?.address?.line1 ?? "");
    setAddressLine2(source?.address?.line2 ?? "");
    setAddressPostalCode(source?.address?.postal_code ?? "");
    setAddressSiteDescription(source?.address?.site_description ?? "");
    setAddressGeo({
      regionId: source?.address?.region_id ?? undefined,
      provinceId: source?.address?.province_id ?? undefined,
      municipalityId: source?.address?.municipality_id ?? undefined,
      barangayId: source?.address?.barangay_id ?? undefined,
    });
    const latitudeValue = source?.address?.latitude;
    const longitudeValue = source?.address?.longitude;
    setAddressCoordinates({
      latitude: typeof latitudeValue === "number" ? latitudeValue : undefined,
      longitude: typeof longitudeValue === "number" ? longitudeValue : undefined,
    });
  }, []);

  const ensureManagedStatus = useCallback((value?: UserStatus | null): ManagedUserStatus => {
    if (value === "suspended" || value === "disabled") {
      return value;
    }
    return "active";
  }, []);

  const syncStatusFromUser = useCallback(
    (source?: User) => {
      if (!source) {
        return;
      }
      setTargetUser(source);
      setStatusForm({
        status: ensureManagedStatus(source.status),
        reason: source.status_reason ?? "",
      });
    },
    [ensureManagedStatus],
  );

  const loadConnections = useCallback(async () => {
    if (!isEditing || !accessToken || !userId) {
      setConnections([]);
      setIsLoadingConnections(false);
      return;
    }

    setIsLoadingConnections(true);
    setConnectionsError(null);

    try {
      const result = await listUserOAuthConnections(accessToken, userId);
      setConnections(result);
    } catch (err) {
      logger.error("Failed to load user OAuth connections", err);
      const message =
        err instanceof ApiError
          ? err.message
          : t("auth.oauth.connected_accounts.loading");
      setConnectionsError(message);
      setConnections([]);
    } finally {
      setIsLoadingConnections(false);
    }
  }, [accessToken, isEditing, t, userId]);

  useEffect(() => {
    if (!isEditing) {
      setIsLoading(false);
      setConnections([]);
      setIsLoadingConnections(false);
      setConnectionsError(null);
      syncAddressFromUser();
      return;
    }

    if (!accessToken || !userId) {
      return;
    }

    const state = location.state as RouteState | undefined;
    if (state?.user && state.user.id === userId) {
      formActions.setMultiple({
        email: state.user.email,
        name: state.user.name ?? "",
        password: "",
        system_role: state.user.system_role,
      });
      syncAddressFromUser(state.user);
      syncStatusFromUser(state.user);
      setIsLoading(false);
      return;
    }

    const loadUser = async () => {
      setIsLoading(true);
      resetFeedback();
      try {
        const fetched = await getUser(accessToken, userId);
        formActions.setMultiple({
          email: fetched.email,
          name: fetched.name ?? "",
          password: "",
          system_role: fetched.system_role,
        });
        syncAddressFromUser(fetched);
        syncStatusFromUser(fetched);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : t("admin.users.form.loading");
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadUser();
  }, [accessToken, formActions, isEditing, location.state, resetFeedback, setError, syncAddressFromUser, syncStatusFromUser, t, userId]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    void loadConnections();
  }, [isEditing, loadConnections]);

  const managedStatusOptions = useMemo(
    () =>
      MANAGED_USER_STATUSES.map((value) => ({
        value,
        label: t(`admin.users.status.options.${value}`),
      })),
    [t],
  );

  const statusDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [i18n.language],
  );

  const formattedStatusChangedAt = useMemo(() => {
    if (!targetUser?.status_changed_at) {
      return t("common.date_unknown");
    }
    const parsed = new Date(targetUser.status_changed_at);
    if (Number.isNaN(parsed.getTime())) {
      return t("common.date_unknown");
    }
    return statusDateFormatter.format(parsed);
  }, [statusDateFormatter, t, targetUser?.status_changed_at]);

  const statusHasChanges = useMemo(() => {
    if (!targetUser) {
      return false;
    }
    const previousReason = (targetUser.status_reason ?? "").trim();
    const nextReason = statusForm.reason.trim();
    return (
      ensureManagedStatus(targetUser.status) !== statusForm.status ||
      previousReason !== nextReason
    );
  }, [ensureManagedStatus, statusForm, targetUser]);

  const submitStatusChange = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!accessToken || !userId || !targetUser || !statusHasChanges) {
        return;
      }

      if (!options?.silent) {
        resetFeedback();
      }
      setIsUpdatingStatus(true);

      try {
        const updated = await updateUserStatus(accessToken, userId, {
          status: statusForm.status,
          reason: statusForm.reason.trim() || undefined,
        });
        syncStatusFromUser(updated);
        if (!options?.silent) {
          setSuccess(t("admin.users.status.toast.updated"));
        }
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : t("common.error.unexpected");
        setError(message);
        throw err;
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [accessToken, resetFeedback, setError, setSuccess, statusForm.reason, statusForm.status, statusHasChanges, syncStatusFromUser, t, targetUser, userId],
  );


  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const pageTitle = isEditing ? t("admin.users.edit.title") : t("admin.users.add.title");
    return [
      { label: t("admin.title"), to: "/admin" },
      { label: t("admin.users.list_title"), to: "/admin/users" },
      { label: pageTitle },
    ];
  }, [isEditing, t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/admin/users" },
  });

  const handleDisconnectProvider = useCallback(
    async (provider: string) => {
      if (!accessToken || !userId) {
        return;
      }

      resetFeedback();
      setConnectionsError(null);
      setDisconnectingProvider(provider);

      try {
        await unlinkUserOAuthProvider(accessToken, userId, provider);
        setSuccess(`${getProviderLabel(provider)} ${t("auth.oauth.disconnect")}.`);
        await loadConnections();
      } catch (err) {
        logger.error(`Failed to disconnect ${provider}`, err);
        const message =
          err instanceof ApiError
            ? err.message
            : t("common.error.unexpected");
        setError(message);
      } finally {
        setDisconnectingProvider(null);
      }
    },
    [accessToken, loadConnections, resetFeedback, setError, setSuccess, t, userId]
  );

  const handleFieldChange =
    (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement>) =>
      formActions.set(field, event.target.value);

  const handleRoleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    formActions.set("system_role", event.target.value as User["system_role"]);
  };

  const handleStatusChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setStatusForm((previous) => ({
      ...previous,
      status: event.target.value as ManagedUserStatus,
    }));
  };

  const handleStatusReasonChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setStatusForm((previous) => ({
      ...previous,
      reason: event.target.value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }

    const trimmedEmail = form.email.trim();
    const trimmedName = form.name.trim();

    if (!trimmedEmail) {
      setError(t("forms.email.required"));
      return;
    }

    if (!isEditing && form.password.length < 8) {
      setError(t("forms.password.min_length"));
      return;
    }

    setIsSubmitting(true);
    resetFeedback();

    try {
      if (isEditing && userId) {
        const payload: AdminUpdateUserPayload = {
          email: trimmedEmail,
          name: trimmedName || undefined,
          system_role: form.system_role,
        };
        const addressPayload: AddressPayload = {
          line1: addressLine1.trim() || undefined,
          line2: addressLine2.trim() || undefined,
          site_description: addressSiteDescription.trim() || undefined,
          postal_code: addressPostalCode.trim() || undefined,
          region_id: addressGeo.regionId,
          province_id: addressGeo.provinceId,
          municipality_id: addressGeo.municipalityId,
          barangay_id: addressGeo.barangayId,
          latitude: typeof addressCoordinates.latitude === "number" ? addressCoordinates.latitude : undefined,
          longitude: typeof addressCoordinates.longitude === "number" ? addressCoordinates.longitude : undefined,
        };
        payload.address = addressPayload;
        await updateUser(accessToken, userId, payload);
        if (statusHasChanges) {
          await submitStatusChange({ silent: true });
        }
        storage.set(ADMIN_USERS_NOTICE_KEY, {
          type: "success",
          message: t("admin.users.toast.updated"),
        });
        navigate("/admin/users", {
          replace: true,
          state: { refreshed: true },
        });
      } else {
        const payload: AdminCreateUserPayload = {
          email: trimmedEmail,
          name: trimmedName || undefined,
          password: form.password,
          system_role: form.system_role,
        };
        await createUser(accessToken, payload);
        storage.set(ADMIN_USERS_NOTICE_KEY, {
          type: "success",
          message: t("admin.users.toast.created"),
        });
        navigate("/admin/users", {
          replace: true,
          state: { refreshed: true },
        });
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("common.error.unexpected");
      setError(message);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setConfirmAction({ type: "cancel" });
  };

  const confirmDialogConfig = useMemo(() => {
    if (!confirmAction) {
      return null;
    }
    if (confirmAction.type === "disconnect-provider") {
      const providerLabel = getProviderLabel(confirmAction.provider);
      return {
        title: `${t("auth.oauth.disconnect")} ${providerLabel}?`,
        confirmLabel: t("auth.oauth.disconnect"),
        loading: disconnectingProvider === confirmAction.provider,
        tone: "danger" as const,
        action: () => handleDisconnectProvider(confirmAction.provider)
      };
    }
    return {
      title: t("common.confirm_discard"),
      confirmLabel: t("actions.confirm", "Confirm"),
      loading: false,
      tone: "danger" as const,
      action: handleBack
    };
  }, [confirmAction, disconnectingProvider, handleBack, handleDisconnectProvider, t]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmDialogConfig) {
      return;
    }
    try {
      await confirmDialogConfig.action();
    } finally {
      setConfirmAction(null);
    }
  }, [confirmDialogConfig]);

  if (!user || !accessToken) {
    return null;
  }

  if (user.system_role !== "admin") {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("common.access_denied.title")}
          subtitle={t("common.access_denied.message")}
          actions={
            <Link to="/dashboard" className={ghostButtonClasses}>
              <SquaresFourIcon {...defaultIconProps} aria-hidden />
              {t("navigation.to_dashboard")}
            </Link>
          }
        />
      </PageShell>
    );
  }

  const pageTitle = isEditing ? t("admin.users.edit.title") : t("admin.users.add.title");
  const pageSubtitle = isEditing
    ? t("admin.users.edit.subtitle")
    : t("admin.users.add.subtitle");
  const statusHelper = t("admin.users.status.description");
  const statusAutoHint = t("admin.users.status.auto_hint");
  const statusBadgeLabel = t(
    `admin.users.status.options.${targetUser?.status ?? "active"}`,
  );
  const statusReason = targetUser?.status_reason?.trim();

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleBack}
          >
            <ArrowLeft {...defaultIconProps} aria-hidden />
            {t("actions.back")}
          </button>
        }
      />

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700" role="status">
          {success}
        </div>
      )}

      {isLoading ? (
        <p className="px-6 py-8 text-center text-slate-500">{t("admin.users.form.loading")}</p>
      ) : (
        <form className={formStackClasses} onSubmit={handleSubmit}>
          <section className={cardClasses}>
            <div className={gridClasses}>
              <div className={fieldClasses}>
                <label className="block" htmlFor="admin-user-email">
                  <FormFieldLabel label={t("forms.email.label")} />
                </label>
              <input
                id="admin-user-email"
                type="email"
                inputMode="email"
                className={inputClasses}
                value={form.email}
                onChange={handleFieldChange("email")}
                  required
                />
              </div>
              <div className={fieldClasses}>
                <label className="block" htmlFor="admin-user-name">
                  <FormFieldLabel label={t("forms.name.label")} optional />
                </label>
                <input
                  id="admin-user-name"
                  type="text"
                  className={inputClasses}
                  value={form.name}
                  onChange={handleFieldChange("name")}
                  placeholder={t("forms.name.optional_placeholder")}
                />
              </div>
              {!isEditing && (
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-password">
                    <FormFieldLabel label={t("forms.password.label")} />
                  </label>
                  <input
                    id="admin-user-password"
                    type="password"
                    className={inputClasses}
                    value={form.password}
                    onChange={handleFieldChange("password")}
                    minLength={8}
                    required
                  />
                  <span className="text-xs text-slate-500">{t("forms.password.min_length_hint")}</span>
                </div>
              )}
              <div className={fieldClasses}>
                <label className="block" htmlFor="admin-user-role">
                  <FormFieldLabel label={t("admin.users.table.system_role")} />
                </label>
                <select
                  id="admin-user-role"
                  className={inputClasses}
                  value={form.system_role}
                  onChange={handleRoleChange}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {isEditing && targetUser && (
            <section className={cardClasses}>
              <div className="mb-4 flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.users.status.title")}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{statusHelper}</p>
              </div>
              <div className="mb-4 flex items-center gap-3">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    targetUser.status === "active" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  {statusBadgeLabel}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {t("admin.users.status.last_changed", { date: formattedStatusChangedAt })}
                </span>
              </div>
              {statusReason && (
                <p className="mb-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                  <strong>{t("admin.users.status.reason_label")}:</strong> {statusReason}
                </p>
              )}
              <div className={gridClasses}>
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-status-select">
                    <FormFieldLabel label={t("admin.users.status.select_label")} />
                  </label>
                  <select
                    id="admin-user-status-select"
                    className={inputClasses}
                    value={statusForm.status}
                    onChange={handleStatusChange}
                    disabled={isUpdatingStatus}
                  >
                    {managedStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-status-reason">
                    <FormFieldLabel
                      label={t("admin.users.status.reason_label")}
                      optional
                    />
                  </label>
                  <textarea
                    id="admin-user-status-reason"
                    className={inputClasses}
                    value={statusForm.reason}
                    onChange={handleStatusReasonChange}
                    rows={3}
                    placeholder={t("admin.users.status.reason_placeholder")}
                    disabled={isUpdatingStatus}
                  />
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500 italic dark:text-slate-400">{statusAutoHint}</p>
            </section>
          )}

          {isEditing && (
            <section className={cardClasses}>
              <div className="mb-6 flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.users.address.title")}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("admin.users.address.description")}</p>
              </div>
              <GeoLocationSelect
                value={addressGeo}
                onChange={setAddressGeo}
                includeBarangay
                disabled={isSubmitting}
              />
              <div className={gridClasses}>
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-address-line1">
                    <FormFieldLabel
                      label={t("admin.users.address.line1_label")}
                      optional
                    />
                  </label>
                  <input
                    id="admin-user-address-line1"
                    type="text"
                    className={inputClasses}
                    value={addressLine1}
                    onChange={(event) => setAddressLine1(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-address-line2">
                    <FormFieldLabel
                      label={t("admin.users.address.line2_label")}
                      optional
                    />
                  </label>
                  <input
                    id="admin-user-address-line2"
                    type="text"
                    className={inputClasses}
                    value={addressLine2}
                    onChange={(event) => setAddressLine2(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-address-postal">
                    <FormFieldLabel
                      label={t("admin.users.address.postal_code_label")}
                      optional
                    />
                  </label>
                  <input
                    id="admin-user-address-postal"
                    type="text"
                    className={inputClasses}
                    value={addressPostalCode}
                    onChange={(event) => setAddressPostalCode(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className={fieldClasses}>
                  <label className="block" htmlFor="admin-user-address-site">
                    <FormFieldLabel
                      label={t("admin.users.address.site_description_label")}
                      optional
                    />
                  </label>
                  <input
                    id="admin-user-address-site"
                    type="text"
                    className={inputClasses}
                    value={addressSiteDescription}
                    onChange={(event) => setAddressSiteDescription(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </section>
          )}

          {isEditing && (
            <section
              className={cardClasses}
              aria-live="polite"
            >
              <div className="mb-6 flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("auth.oauth.connected_accounts.title")}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("auth.oauth.connected_accounts.description")}</p>
              </div>

              {connectionsError && (
                <p
                  className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
                  role="alert"
                >
                  {connectionsError}
                </p>
              )}

              {isLoadingConnections ? (
                <p className="py-8 text-center text-sm text-slate-500">
                  {t("auth.oauth.connected_accounts.loading")}
                </p>
              ) : connections.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">
                  {t("auth.oauth.connected_accounts.empty")}
                </p>
              ) : (
                <ul className="flex flex-col gap-4">
                  {connections.map((connection) => (
                    <li
                      key={connection.id}
                      className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50"
                    >
                      <ConnectedAccountProvider
                        provider={connection.provider}
                        title={getProviderLabel(connection.provider)}
                        description={connection.email}
                      />
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-700"
                        onClick={() =>
                          setConfirmAction({
                            type: "disconnect-provider",
                            provider: connection.provider,
                          })
                        }
                        disabled={
                          disconnectingProvider === connection.provider
                        }
                      >
                        {disconnectingProvider === connection.provider ? (
                          t("auth.oauth.disconnecting")
                        ) : (
                          <>
                            <LinkSimpleBreak {...defaultIconProps} aria-hidden />
                            {t("auth.oauth.disconnect")}
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleCancel}
            >
              <XCircle {...defaultIconProps} aria-hidden />
              {t("actions.cancel")}
            </button>
            <button
              type="submit"
              className={primaryButtonClasses}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                t("actions.saving")
              ) : (
                <>
                  <FloppyDisk {...defaultIconProps} aria-hidden />
                  {isEditing ? t("actions.save_changes") : t("admin.users.add.button")}
                </>
              )}
            </button>
          </div>
        </form>
      )}
      <ConfirmDialog
        open={Boolean(confirmDialogConfig)}
        title={confirmDialogConfig?.title ?? ""}
        confirmLabel={confirmDialogConfig?.confirmLabel ?? ""}
        cancelLabel={t("actions.cancel")}
        confirmTone={confirmDialogConfig?.tone ?? "default"}
        confirmLoading={Boolean(confirmDialogConfig?.loading)}
        onCancel={() => {
          if (confirmDialogConfig?.loading) return;
          setConfirmAction(null);
        }}
        onConfirm={handleConfirmAction}
      />
    </PageShell>
  );
}
